import React, {FormEvent} from 'react';
import Toggle from "components/Form/Toggle";
import Field from "components/Form/Field";
import {FormikHelpers} from "formik";
import {MembershipFilter as MembershipFilterSpec} from "types/AudienceCriteria";
import {setFilterDates} from "components/Form/CriteriaBuilder/Filters/Filter";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import TicketStatus from "types/TicketStatus";
import {useOperator} from "../../../../context/OperatorContext";

export type Props = {
    formik: FormikHelpers<any>
    groupIndex: number
    filter?: MembershipFilterSpec | null
}

const membershipFilterTemplate: MembershipFilterSpec = {
    not: false,
    plan_id: 0,
    status: "any",
    mode: "ever",
    dates: [],
}

function MembershipFilter({formik, groupIndex, filter}: Props) {
    const {termFor} = useOperator();

    const {data: plans, isLoading: plansLoading} = useQuery(
        ['membership-plans.all', {page: 1, perPage: 500}],
        () => ngage.membershipPlans.all(1, 500)
    )

    // Handle toggling this filter on and off
    const handleToggle = (checked: boolean): void => {
        formik.setFieldValue(
            `criteria.groups.${groupIndex}.filters.membership`,
          checked ? {...membershipFilterTemplate} : undefined
        )
    }

    // Handling changing the "not" state
    const handleChangeNot = (e: FormEvent<HTMLSelectElement>) => {
        formik.setFieldValue(
            `criteria.groups.${groupIndex}.filters.membership.not`,
            e.currentTarget.value === 'true'
        )
    }

    // Handling changing the date mode
    const handleChangeMode = (e: FormEvent<HTMLSelectElement>) => {
        formik.setFieldValue(`criteria.groups.${groupIndex}.filters.membership.mode`, e.currentTarget.value)

        setFilterDates(formik,
            `criteria.groups.${groupIndex}.filters.membership.dates`,
            e.currentTarget.value,
            filter?.dates || []
        )
    }

    return (
        <div className="mt-8">
            <div className="flex items-center">
                <Toggle
                  label={termFor('Membership')}
                  checked={!!filter}
                  onChange={handleToggle}
                  loading={plansLoading}
                  disabled={plansLoading}
                />

                {!filter && <span className="font-medium">...</span>}
            </div>

            {!!filter &&
                <div className="mt-2 pl-14 lg:flex items-center">

                    {/* Not */}
                    <div className="flex-shrink-0">
                        <Field as="select"
                               name={`criteria.groups.${groupIndex}.filters.membership.not`}
                               disabled={!filter}
                               value={filter.not? 'true' : 'false'}
                               onChange={handleChangeNot}
                        >
                            <option value="false">Holds</option>
                            <option value="true">Doesn't hold</option>
                        </Field>
                    </div>

                    {/* Plan */}
                    <div className="mt-2 lg:mt-0 lg:w-48 lg:ml-3 flex-shrink-0">
                        <Field as="select"
                               name={`criteria.groups.${groupIndex}.filters.membership.plan_id`}
                               disabled={!filter}
                        >
                            <option value="0" key={0}>any</option>
                            {plans?.data.map(membershipPlan => (
                                <option value={membershipPlan.id} key={membershipPlan.id}>
                                    {membershipPlan.name}
                                </option>
                            ))}
                        </Field>
                    </div>

                    <span className="ml-1 lg:mt-0 lg:ml-3 flex-shrink-0">
                        {termFor('membership')}
                    </span>

                    {/* Status */}
                    <div className="lg:ml-3 flex-shrink-0">
                        <Field as="select"
                               name={`criteria.groups.${groupIndex}.filters.membership.status`}
                               disabled={!filter}
                        >
                            <option value="any">any status</option>
                            <option value={TicketStatus.VALID}>valid</option>
                            <option value={TicketStatus.EXPIRED}>expired</option>
                            <option value={TicketStatus.PAID}>paid</option>
                            <option value={TicketStatus.UNPAID}>unpaid</option>
                            <option value={TicketStatus.REVOKED}>revoked</option>
                        </Field>
                    </div>

                    {/* Mode */}
                    <div className="mt-2 lg:mt-0 lg:ml-3 flex-shrink-0">
                        <Field as="select"
                               name={`criteria.groups.${groupIndex}.filters.membership.mode`}
                               disabled={!filter}
                               onChange={handleChangeMode}>
                            <option value="ever">ever</option>
                            <option value="today">today</option>
                            <option value="tomorrow">tomorrow</option>
                            <option value="yesterday">yesterday</option>
                            <option value="this-week">this week</option>
                            <option value="next-week">next week</option>
                            <option value="last-week">last week</option>
                            <option value="on">on</option>
                            <option value="between">between</option>
                            <option value="before">before</option>
                            <option value="after">after</option>
                        </Field>
                    </div>

                    {/* Date 1 */}
                    <div className="mt-2 lg:mt-0 lg:ml-3 flex-shrink-0">
                        {['on', 'before', 'after', 'between'].includes(filter?.mode || '') &&
                            <Field type="date"
                                   name={`criteria.groups.${groupIndex}.filters.membership.dates.0`}
                            />
                        }
                    </div>

                    {/* Date 2 */}
                    <div className="lg:ml-3 lg:flex items-center flex-shrink-0">
                        {['between'].includes(filter?.mode || '') &&
                            <>
                                <span className="ml-1 lg:ml-0 lg:mr-3">and</span>
                                <Field type="date"
                                       name={`criteria.groups.${groupIndex}.filters.membership.dates.1`}
                                />
                            </>
                        }
                    </div>
                </div>
            }
        </div>
    );
}

export default MembershipFilter;